/**
 * Currency formatting utilities
 */
import { DEFAULT_LANGUAGE_TAG } from "./localeUtils";
/**
 * Format amount with the appropriate currency using Intl.NumberFormat
 * @param amount - Amount in cents
 * @param currency - ISO 4217 currency code (e.g. EUR, USD, CAD)
 * @param locale - BCP 47 locale tag (e.g. fr-FR, en-US). Defaults to navigator language or fr-FR.
 * @returns Formatted amount with currency symbol (e.g. "30,00 €", "$30.00")
 */
export const formatCurrency = (amount, currency, locale) => {
    if (amount === undefined)
        return "0.00";
    const units = amount / 100;
    if (!currency)
        return units.toFixed(2);
    const resolvedLocale = locale ?? navigator.language ?? DEFAULT_LANGUAGE_TAG;
    try {
        return new Intl.NumberFormat(resolvedLocale, {
            style: "currency",
            currency,
            currencyDisplay: "narrowSymbol",
        }).format(units);
    }
    catch {
        return `${units.toFixed(2)} ${currency}`;
    }
};
/**
 * Get currency symbol for a currency code using Intl.NumberFormat
 * @param currency - ISO 4217 currency code (e.g. EUR, USD, CAD)
 * @param locale - BCP 47 locale tag (e.g. fr-FR, en-US). Defaults to navigator language or fr-FR.
 * @returns The currency symbol (e.g. €, $, CA$) or the currency code if resolution fails
 */
export const getCurrencySymbol = (currency, locale) => {
    if (!currency)
        return "";
    const resolvedLocale = locale ?? navigator.language ?? DEFAULT_LANGUAGE_TAG;
    try {
        const parts = new Intl.NumberFormat(resolvedLocale, {
            style: "currency",
            currency,
            currencyDisplay: "narrowSymbol",
        }).formatToParts(0);
        return parts.find((p) => p.type === "currency")?.value ?? currency;
    }
    catch {
        return currency;
    }
};
